/**
 * useClassificationLookup Hook
 *
 * Searches ContentSearch by subjectId and maps the result to a ClassificationResult
 * for display in the ClassificationResultDialog from classification-testing.
 *
 * On-demand only: fetches classification data when the user clicks "Classification"
 * in the action menu. Opens the dialog immediately with a loading state, then shows
 * the result or "no data" when the search completes.
 *
 * @see {@link docs/development/feature-moderation-classification-lookup.md}
 */

import type { ClassificationResult } from '@classification-testing/domain';
import { ContentType, ClassificationType } from '@classification-testing/domain';
import type { ContentSearchItem, ClassificationResults as ContentSearchClassification, ContentSearchPolicy } from '@content-search/domain';
import { useContentSearch } from '@content-search/hooks';
import { toClassificationDialogData } from '@moderation/adapters';
import { useState, useCallback, useEffect, useRef, useMemo } from 'react';

/**
 * Maps a raw contentType string to the ContentType enum
 */
function mapContentTypeToEnum(contentType: string): ContentType {
  switch (contentType?.toLowerCase()) {
    case 'image':
      return ContentType.IMAGE;
    case 'audio':
      return ContentType.AUDIO;
    case 'username':
      return ContentType.USERNAME;
    case 'mixed':
      return ContentType.MIXED;
    default:
      return ContentType.TEXT;
  }
}

/**
 * Maps a raw classificationType string to the ClassificationType enum
 */
function mapClassificationType(type?: string): ClassificationType {
  if (type === 'safe_content') {
    return ClassificationType.SAFE_CONTENT;
  }

  return ClassificationType.HARMFUL_CONTENT;
}

/**
 * Maps a camelCase policy from ContentSearch to the snake_case format expected by ClassificationResult
 */
function mapPolicy(policy: ContentSearchPolicy) {
  return {
    id: policy.id,
    type: policy.type,
    created_by: policy.createdBy,
    created_at: policy.createdAt,
    last_modified_by: policy.lastModifiedBy,
    last_modified_at: policy.lastModifiedAt,
    community_id: policy.communityId,
    is_enabled: policy.isEnabled,
    name: policy.name,
    description: policy.description,
    labels: policy.labels.map(l => ({
      label_id: l.labelId,
      severity: l.severity,
    })),
  };
}

/**
 * Maps a ContentSearchItem to the ClassificationResult shape expected by ClassificationResultDialog.
 *
 * Uses typed {@link ContentSearchClassification} for compile-time safety instead of
 * raw `Record<string, unknown>` casting.
 */
function mapContentSearchToClassificationResult(item: ContentSearchItem): ClassificationResult {
  const classResults = item.classificationResults as ContentSearchClassification | null | undefined;

  // Guard: classificationResults may be null/undefined from upstream
  if (!classResults) {
    return {
      id: item.id,
      contentType: mapContentTypeToEnum(item.contentType),
      classificationType: ClassificationType.HARMFUL_CONTENT,
      labels: [],
      timestamp: item.createdAt,
      confidenceScore: 0,
      languageDetected: '',
      text: '',
      policies: [],
      nonTriggeredPolicies: [],
    };
  }

  // Map labels from camelCase (ContentSearch) to snake_case (ClassificationResult)
  // Use each label's own contentType (e.g., image items may have both "image" and "text" labels)
  // Fall back to item-level contentType if the label doesn't have its own
  const labels = (classResults.labels || []).map(label => ({
    severity: Number(label.severity) || 0,
    label_num_map: Number(label.labelNumMap) || 0,
    label_name: String(label.labelName || ''),
    label_short_name: String(label.labelShortName || ''),
    label_id: label.labelId,
    score: label.score,
    content_type: label.contentType || item.contentType,
  }));

  // Map triggered and non-triggered policies
  const policies = (classResults.policies || []).map(mapPolicy);
  const nonTriggeredPolicies = (classResults.nonTriggeredPolicies || []).map(mapPolicy);

  return {
    id: item.id,
    contentType: mapContentTypeToEnum(item.contentType),
    classificationType: mapClassificationType(classResults.classificationType),
    labels,
    timestamp: item.createdAt,
    confidenceScore: classResults.confidenceScore,
    languageDetected: classResults.languageDetected,
    languageClassified: classResults.languageClassified,
    wasTranslated: classResults.wasTranslated,
    text: classResults.transcript || '',
    duration: classResults.processingTimeMs,
    categories: classResults.categories,
    explanation: classResults.explanation,
    policies,
    nonTriggeredPolicies,
    contentUrl: item.contentMetadata?.contentUrl as string | undefined,
  };
}

/**
 * Hook that provides on-demand classification lookup for moderation cases.
 *
 * Call `lookupClassification(communityId, subjectId)` when the user clicks
 * "Classification" in the action menu. The dialog opens immediately with a
 * loading spinner; when the search completes it shows either the full
 * classification result or a "no data" message.
 */
export function useClassificationLookup() {
  const { searchResult, isSearching, errors, search } = useContentSearch();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [classificationResult, setClassificationResult] = useState<ClassificationResult | null>(null);
  const [isContentBlurred, setIsContentBlurred] = useState(true);
  const [searchCompleted, setSearchCompleted] = useState(false);

  // Ref to track whether a lookup is active (prevents stale effect triggers)
  const lookupActive = useRef(false);

  // When search results arrive, map the first result or leave null for "no data"
  useEffect(() => {
    if (isSearching || !lookupActive.current) return;

    lookupActive.current = false;
    setSearchCompleted(true);

    if (searchResult.results.length > 0) {
      const mapped = mapContentSearchToClassificationResult(searchResult.results[0]);
      const safe = mapped.classificationType === ClassificationType.SAFE_CONTENT;

      setClassificationResult(mapped);
      setIsContentBlurred(!safe);
    } else {
      // Reset to null so the dialog shows "no data" instead of stale results
      setClassificationResult(null);
    }
  }, [searchResult, isSearching]);

  /**
   * Triggers a content search by subjectId and opens the dialog immediately.
   * The dialog shows a loading spinner until results arrive, then either the
   * classification data or a "no data" message.
   */
  const lookupClassification = useCallback(
    (communityId: string, subjectId: string) => {
      // Reset state for fresh lookup
      setClassificationResult(null);
      setSearchCompleted(false);
      setIsContentBlurred(true);

      // Open dialog immediately (shows loading state)
      setIsDialogOpen(true);

      lookupActive.current = true;
      search({ communityId, subjectId, maxResultsPerBatch: 1 }, 'network-only');
    },
    [search]
  );

  const closeDialog = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  const toggleBlur = useCallback(() => {
    setIsContentBlurred(prev => !prev);
  }, []);

  const isSafe = useMemo(() => classificationResult?.classificationType === ClassificationType.SAFE_CONTENT, [classificationResult]);

  // Convert to canonical ClassificationDialogData format for core component
  const classificationDialogData = useMemo(() => (classificationResult ? toClassificationDialogData(classificationResult) : null), [classificationResult]);

  return {
    lookupClassification,
    classificationDialogData,
    isDialogOpen,
    isSearching,
    searchCompleted,
    errors,
    isSafe,
    isContentBlurred,
    closeDialog,
    toggleBlur,
  };
}
